import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AuthenticationContext } from "../../components/authentication/AuthenticationContext";
import { Box, Button, MenuItem, Select, Stack, TextField } from "@mui/material";
import ButtonBar from "../../components/button-bar/ButtonBar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faCloudArrowUp } from "@fortawesome/free-solid-svg-icons";
import { deleteArtistPhoto, postArtistPhotoRatingChange, getArtistPhotos, postArtistPhoto } from "../../components/apis/music-api";
import PhotoRow from "./PhotoRow";
import callApi from "../../components/call-api/call-api";
import { BeatLoader } from "react-spinners";
import { PhotoRating } from "../../types/PhotoRating";

type ManagePhotosProps = {
    letter: string,
    artistName: string,
}

const ManagePhotos = () => {
    const params = useParams<ManagePhotosProps>();
    const { getAccessToken } = useContext(AuthenticationContext);
    const [ photos, setPhotos ] = useState<string[]>([]);
    const [ posting, setPosting ] = useState<boolean>(false);
    const [ selectedFile, setSelectedFile ] = useState<File | null>(null);
    const [ rating, setRating ] = useState<PhotoRating>(PhotoRating.General);
    const [ isProcessing, setIsProcessing ] = useState<boolean>(false);
    
    useEffect(() => {
        callApi<string[]>(getArtistPhotos(getAccessToken(), params.artistName ?? '', PhotoRating.Explicit), setPhotos);
    }, [params, getAccessToken]);

    useEffect(() => {
        if (!posting) {
            callApi<string[]>(getArtistPhotos(getAccessToken(), params.artistName ?? '', PhotoRating.Explicit), setPhotos);
        }
    }, [posting]);
                    
    const deletePhoto = async (photoName: string) => {
        await deleteArtistPhoto(getAccessToken(), params.artistName ?? '', photoName ?? '');
        setTimeout(() => callApi<string[]>(getArtistPhotos(getAccessToken(), params.artistName ?? '', PhotoRating.Explicit), setPhotos), 1000);
    }
    
    const handleDelete = (name: string) => {
        if (params.letter !== undefined && params.artistName !== undefined) {
            deletePhoto(name);
        }
    }

    const handleFileChange  = (event: ChangeEvent<HTMLInputElement>) => { 
        if (event.target.files && event.target.files.length > 0) {
            setSelectedFile(event.target.files[0]);
        }
    }
    
    const fileToUint8Array = async (file: File): Promise<Uint8Array> => {
        const arrayBuffer = await file.arrayBuffer();
        return new Uint8Array(arrayBuffer);
    }
    
    const handleUpload = () => {   
        if (selectedFile) {
            fileToUint8Array(selectedFile).then((f) => {
                const formData = new FormData();
                const byteArray = new Uint8Array(f);
                const blob = new Blob([byteArray]);
                formData.append('file', blob, 'cover.jpg');
                if (params.letter !== undefined && params.artistName !== undefined) {
                    callApi(postArtistPhoto(getAccessToken(), params.artistName ?? '', rating, formData), () => {}, setPosting);
                }
            });

            setSelectedFile(null); // Reset the selected file after upload
        }
    };

    const handleRatingChange = (photoName: string, rating: PhotoRating) => {
        if (params.letter !== undefined && params.artistName !== undefined) {
            setIsProcessing(true);
            callApi(postArtistPhotoRatingChange(getAccessToken(), params.artistName ?? '', photoName, rating), () => {
                callApi<string[]>(getArtistPhotos(getAccessToken(), params.artistName ?? '', PhotoRating.Explicit), setPhotos);
                setIsProcessing(false);
            });
        }
    }
    
    return (
        <>
            <ButtonBar leftButtons={[   
                <Button component={Link} variant="contained" size="small" to={`/letters/${params.letter}/artists/${params.artistName}`}>
                    <FontAwesomeIcon fontSize="small" color="primary" icon={faArrowLeft} className="icon"/>
                    Back To Artist
                </Button>
            ]} />
            <Stack direction="row" spacing={2}>
                <Box>
                    <Stack spacing={3}>
                        {photos?.map((photoName: string, index: number) =>
                            <Box sx={{ padding: '6px' }} key={`$photo-${index}`}>
                                <PhotoRow
                                    key={`photo-${index}`} 
                                    letter={params.letter ?? ''}
                                    artistName={params.artistName ?? ''}
                                    photoName={photoName}
                                    deletePhoto={handleDelete}
                                    onRatingChange={handleRatingChange}
                                    isProcessing={isProcessing}
                                />
                            </Box>
                        )}
                        {!posting &&
                            <Box sx={{ padding: '6px' }} key={`photo-add`}>
                                <Stack direction="row" spacing={3}>
                                    <Box>
                                        <TextField type="file" variant="outlined" size="small" margin="none" onChange={handleFileChange} />
                                    </Box>
                                    <Box>
                                        <Select size="small" value={rating} onChange={(e) => setRating(e.target.value as PhotoRating)}>
                                            <MenuItem value={PhotoRating.General}>G</MenuItem>
                                            <MenuItem value={PhotoRating.Restricted}>R</MenuItem>
                                            <MenuItem value={PhotoRating.Explicit}>X</MenuItem>
                                        </Select>
                                    </Box> 
                                    <Box> 
                                        <Button variant="contained" color="primary" onClick={handleUpload} disabled={!selectedFile}>
                                            <FontAwesomeIcon fontSize="large" color="primary" icon={faCloudArrowUp} className="icon"/>
                                            Upload Photo
                                        </Button>
                                    </Box>
                                </Stack>
                            </Box>
                        }
                        <Stack direction="row" spacing={2} justifyContent="center" alignItems="flex-start" sx={{ marginTop: '20px' }}>
                            <Box alignContent="center">
                                <BeatLoader
                                    loading={posting}
                                    size={24}
                                    aria-label="Loading Spinner"
                                    style={{ textAlign: 'center' }}
                                    data-testid="loader" />
                            </Box>
                        </Stack>
                    </Stack>
                </Box>
            </Stack>
        </>
    );
}

export default ManagePhotos;